<template>
    <div class="product-details">
        <product-hero
            :name="name"
            :description="description"
            :calories="calories"
            :weight="weight"
            :image="image"
            :image-sm="imageSm"
            :dietary-labels="dietaryLabels"
            :product-type="productType"
            :button-text="heroButtonText"
            :button-link="heroButtonLink"
            :has-story="Boolean(storyTitle && storyContent)"
        />

        <plant-points-info
            v-if="Object.keys(plantPoints).length"
            :points="plantPoints.points"
            :description="plantPoints.description"
            :image="plantPoints.image"
            :background="plantPoints.background"
        />

        <hr v-else />

        <allergen-info
            :contains="contains"
            :may-contain="mayContain"
            :warning="allergenWarning"
            :nutrition-facts="productType === 'product' ? nutritionFacts : []"
        />

        <nutrition-info
            v-if="productType === 'grocery' && nutritionFacts.length"
            :text="nutritionText"
            :facts="nutritionFacts"
        />

        <div
            v-if="(pdfButtonText && pdfButtonLink) || ingredients || showSoySauceModalButton"
            class="buttons"
        >
            <div
                v-if="ingredients"
                class="button"
            >
                <b-button
                    variant="primary"
                    class="open-ingredients-modal-btn"
                    v-b-modal.ingredients-modal
                    v-test:openIngredientsModalBtn
                >
                    <span class="title">
                        {{ $snippet('open_ingredients_button_text', 'open ingredients') }}
                    </span>
                </b-button>

                <ingredients-modal
                    :ingredients="ingredients"
                    :disclaimer="ingredientsDisclaimer"
                />
            </div>

            <div
                v-if="pdfButtonText && pdfButtonLink"
                class="button"
            >
                <b-button
                    :href="pdfButtonLink"
                    class="pdf-link-btn"
                    variant="primary"
                    target="_blank"
                    rel="noopener"
                    v-test:pdfButton
                >
                    {{ pdfButtonText }}
                </b-button>
            </div>

            <div
                v-if="showSoySauceModalButton"
                class="button"
            >
                <b-button
                    variant="primary"
                    class="open-soy-sauce-modal-btn"
                    v-b-modal.soy-sauce-modal
                    v-test:openSoySauceModalBtn
                >
                    <span class="title">
                        {{ $snippet('open_soy_sauce_modal_button_text', 'soy sauce guide*') }}
                    </span>
                </b-button>

                <soy-sauce-modal />
            </div>
        </div>

        <info-collapse
            v-if="productType === 'grocery' && howToEnjoyTitle && howToEnjoyContent"
            id="how-to-enjoy"
            class="how-to-enjoy"
            :title="howToEnjoyTitle"
            coloured
            centered
            v-test:howToEnjoy
        >
            <markdown-content
                :content="howToEnjoyContent"
                v-test:howToEnjoyMarkdownContent
            />
        </info-collapse>

        <story-collapse
            v-if="storyTitle && storyContent"
            :title="storyTitle"
            :content="storyContent"
        />

        <grocery-product-retailers
            v-if="retailers.length"
            :retailers="retailers"
            id="retailers"
        />
    </div>
</template>

<script>
    import { VBModal } from 'bootstrap-vue';
    import InfoCollapse from '../../molecules/InfoCollapse/InfoCollapse';
    import StoryCollapse from '../../molecules/StoryCollapse/StoryCollapse';
    import IngredientsModal from '../IngredientsModal/IngredientsModal';
    import NutritionInfo from '../../molecules/NutritionInfo/NutritionInfo';
    import AllergenInfo from '../../molecules/AllergenInfo/AllergenInfo';
    import ProductHero from '../../molecules/ProductHero/ProductHero';
    import MarkdownContent from '../../utils/MarkdownContent/MarkdownContent';
    import GroceryProductRetailers from '../GroceryProductRetailers/GroceryProductRetailers';
    import PlantPointsInfo from '../../molecules/PlantPointsInfo/PlantPointsInfo';

    //eslint-disable-next-line @netsells/require-jsdoc-except/require-jsdoc
    const SoySauceModal = () => import('../SoySauceModal/SoySauceModal');

    export default {
        name: 'product-details',

        components: {
            GroceryProductRetailers,
            ProductHero,
            AllergenInfo,
            NutritionInfo,
            IngredientsModal,
            StoryCollapse,
            InfoCollapse,
            MarkdownContent,
            SoySauceModal,
            PlantPointsInfo,
        },

        directives: {
            'b-modal': VBModal,
        },

        props: {
            name: {
                type: String,
                required: true,
            },

            description: {
                type: String,
                default: '',
            },

            calories: {
                type: [Number, String],
                required: true,
            },

            weight: {
                type: String,
                default: '',
            },

            image: {
                type: Object,
                required: true,
            },

            imageSm: {
                type: Object,
                default: () => ({}),
            },

            dietaryLabels: {
                type: Array,
                default: () => ([]),
            },

            productType: {
                type: String,
                default: 'product',
            },

            contains: {
                type: String,
                default: '',
            },

            allergenWarning: {
                type: String,
                default: '',
            },

            mayContain: {
                type: String,
                default: '',
            },

            nutritionText: {
                type: String,
                default: '',
            },

            nutritionFacts: {
                type: Array,
                default: () => ([]),
            },

            plantPoints: {
                type: Object,
                default: () => ({}),
            },

            ingredients: {
                type: String,
                default: '',
            },

            ingredientsDisclaimer: {
                type: String,
                default: '',
            },

            storyTitle: {
                type: String,
                default: '',
            },

            storyContent: {
                type: String,
                default: '',
            },

            howToEnjoyTitle: {
                type: String,
                default: '',
            },

            howToEnjoyContent: {
                type: String,
                default: '',
            },

            heroButtonLink: {
                type: String,
                required: true,
            },

            heroButtonText: {
                type: String,
                required: true,
            },

            retailers: {
                type: Array,
                default: () => ([]),
            },

            pdfButtonText: {
                type: String,
                default: '',
            },

            pdfButtonLink: {
                type: String,
                default: '',
            },

            showSoySauceModalButton: {
                type: Boolean,
                default: false,
            },
        },
    };
</script>

<style lang="scss" scoped>
    .product-details {
        background-color: inherit;
    }

    .product-hero {
        margin-bottom: 28px;

        @include media-breakpoint-up(md) {
            margin-bottom: 16px;
        }
    }

    .plant-points,
    .separator {
        margin-top: 0;
        margin-bottom: 30px;
    }

    .open-ingredients-modal-btn,
    .pdf-link-btn,
    .open-soy-sauce-modal-btn {
        min-width: 196px;
    }

    .allergen-info {
        margin-bottom: 30px;

        @include media-breakpoint-up(md) {
            margin-bottom: 20px;
        }
    }

    .how-to-enjoy {
        ::v-deep .content {
            padding-left: 0;
            padding-right: 0;
        }

        .markdown-content ::v-deep p {
            font-size: rem(20px);
            line-height: rem(24px);
            font-weight: 400;
        }
    }

    .nutrition-info {
        padding-bottom: 25px;

        @include media-breakpoint-up(md) {
            padding-top: 20px;
            border-top: 1px solid secondary-palette(6);
            padding-bottom: 48px;
        }
    }

    .grocery-product-retailers {
        margin: 30px 0;
    }

    .buttons {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        margin: -10px;
        margin-bottom: 25px;
        max-width: 500px;

        @include media-breakpoint-up(md) {
            margin-bottom: 30px;
            max-width: unset;
            justify-content: flex-start;
        }

        > * {
            padding: 10px;
        }
    }
</style>
